import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '../../../../ui/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../../../ui/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../../../ui/components/ui/form';
import { Input } from '../../../../ui/components/ui/input';
import { Textarea } from '../../../../ui/components/ui/textarea';
import { CreateProductRequestDto } from '../../types/request';
import { BillingTermMetaResponseDto, ProductDetailsResponseDto } from '../../types/response';
import BillingTermPriceRow from '../billing-term-price-row/billing-term-price-row';
import { buildProductFormDefaults, productFormSchema, ProductFormValues, toProductRequest } from './product-form.schema';

export interface ProductFormDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Billing terms from `GET products/admin/meta`. */
  billingTerms: BillingTermMetaResponseDto[];
  /** Edit mode when set. */
  product?: ProductDetailsResponseDto | null;
  /** Resolves `true` on success; the dialog only closes then. */
  onSubmit: (payload: CreateProductRequestDto) => Promise<boolean>;
}

const ProductFormDialog = ({ isOpen, onOpenChange, billingTerms, product, onSubmit }: ProductFormDialogProps) => {
  const isEdit = !!product;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<ProductFormValues>({
    resolver: zodResolver(productFormSchema),
    defaultValues: buildProductFormDefaults(billingTerms, product)
  });

  useEffect(() => {
    if (!isOpen) return;
    form.reset(buildProductFormDefaults(billingTerms, product));
  }, [isOpen, billingTerms, product, form]);

  const handleSubmit = async (values: ProductFormValues) => {
    setIsSubmitting(true);
    try {
      const isSuccess = await onSubmit(toProductRequest(values));
      if (isSuccess) onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const pricesError = form.formState.errors.prices;
  const pricesRootMessage = pricesError?.root?.message ?? pricesError?.message;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg min-w-[600px] py-4 px-6 gap-5 bg-white" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="text-[25px] font-bold text-[#12282A]">{isEdit ? 'Produkt bearbeiten' : 'Neues Produkt'}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-5">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem className="flex flex-col gap-[10px]">
                  <FormLabel className="text-[14px] font-bold text-[#12282A]">Titel *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="z. B. 7i7® Design Flatrate (1 Slot)" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem className="flex flex-col gap-[10px]">
                  <FormLabel className="text-[14px] font-bold text-[#12282A]">Beschreibung</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Einfacher Text, Zeilenumbrüche bleiben erhalten" className="min-h-32" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="capacity"
              render={({ field }) => (
                <FormItem className="flex flex-col gap-[10px]">
                  <FormLabel className="text-[14px] font-bold text-[#12282A]">Kapazität (Slots) *</FormLabel>
                  <FormControl>
                    <Input {...field} type="number" min={1} step={1} className="w-32" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-col gap-[10px]">
              <span className="text-[14px] font-bold text-[#12282A]">Laufzeiten & Preise *</span>
              <p className="text-[12px] text-[#888]">Abgerechnet wird immer monatlich – die Laufzeit ist die Mindestlaufzeit.</p>
              {billingTerms.map(({ term, months }, index) => (
                <BillingTermPriceRow key={term} control={form.control} index={index} months={months} />
              ))}
              {pricesRootMessage && <p className="text-[0.8rem] font-medium text-destructive">{pricesRootMessage}</p>}
            </div>

            <DialogFooter className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Speichert…' : isEdit ? 'Speichern' : 'Erstellen'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default ProductFormDialog;
